import { useRef, useEffect, useCallback } from 'preact/hooks';

/**
 * CandleFlames. Animated flames positioned over visible candles in any
 * scene's background art. Each flame has independent flicker, sway, and
 * vertical-scale breathing so they look like real fire rather than synced
 * pulses. Scenes pass their own candle list (usually via CandlePlacementDev).
 */

export default function CandleFlames({ candles = [] }) {
  const flameRefs = useRef([]);
  const glowRefs = useRef([]);
  const rafRef = useRef(null);
  const startRef = useRef(performance.now());
  const candlesRef = useRef(candles);
  candlesRef.current = candles;

  const tick = useCallback(() => {
    const t = (performance.now() - startRef.current) / 1000;
    const current = candlesRef.current;
    for (let i = 0; i < current.length; i++) {
      const c = current[i];
      const flame = flameRefs.current[i];
      const glow = glowRefs.current[i];
      if (!flame) continue;

      // Per-candle "personality." Derive stable jitter constants from
      // the phase so each flame flickers and leans on its own schedule.
      // Mixing irrational multipliers keeps the period long enough that
      // neighbouring flames never settle into a visible synchrony.
      const ph = c.phase;
      const speedJitter = 0.85 + 0.3 * (0.5 + 0.5 * Math.sin(ph * 13.1));
      const flickerFreq = 2.0 + 0.9 * Math.sin(ph * 3.7);
      const swayFreqA = 0.55 + 0.25 * Math.sin(ph * 7.3);
      const swayFreqB = 1.10 + 0.40 * Math.sin(ph * 11.1);
      const swayAmpDeg = 3.0 + 2.0 * (0.5 + 0.5 * Math.sin(ph * 5.9));

      const s = t * c.speed * speedJitter + ph;

      // Three-layer flicker. Incommensurate ratios so the sum doesn't
      // visibly repeat. Range is roughly 0.55–1.0.
      const flicker = 0.65 + 0.35 * (
        0.45 * Math.sin(s * flickerFreq) +
        0.30 * Math.sin(s * flickerFreq * 1.73 + ph) +
        0.25 * Math.sin(s * flickerFreq * 2.41 + ph * 1.7)
      );

      // Lean angle in degrees. skewX with transform-origin: bottom center
      // pivots at the flame's base, so the wick stays planted while the
      // tip drifts side to side. Like a real flame.
      const leanDeg = swayAmpDeg * (
        0.70 * Math.sin(s * swayFreqA + ph) +
        0.30 * Math.sin(s * swayFreqB + ph * 1.4)
      );

      const scaleY = 0.82 + 0.30 * flicker;
      flame.style.opacity = String(0.85 * flicker);
      // -78%Y aligns the radial gradient's bright peak (positioned at 78%
      // down from the flame box's top) with the anchor point. So the
      // handle dot in the dev tool lands exactly where the fire glows.
      // skewX leans the top from the anchored bottom (vs translateX which
      // would slide the whole flame, base and all).
      flame.style.transform = `translate(-50%, -78%) skewX(${leanDeg}deg) scaleY(${scaleY})`;
      // Glow breathes with the flame but at slightly reduced range so it
      // reads as ambient light rather than another moving element.
      if (glow) glow.style.opacity = String(0.28 + 0.12 * flicker);
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    startRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      flameRefs.current = [];
      glowRefs.current = [];
    };
  }, [tick]);

  return (
    <>
      {candles.map((c, i) => (
        <div key={i} className="absolute pointer-events-none" style={{ left: c.left, top: c.top, zIndex: 2 }}>
          {/* Ambient glow. Softens into surrounding art */}
          <div
            ref={(el) => { glowRefs.current[i] = el; }}
            style={{
              position: 'absolute',
              left: `-${c.glow / 2 / 16}cqw`,
              // 60% matches the radial gradient's peak at `50% 60%` so the
              // brightest glow sits on the anchor point.
              top: `-${c.glow * 0.6 / 16}cqw`,
              width: `${c.glow / 16}cqw`,
              height: `${c.glow / 16}cqw`,
              background: 'radial-gradient(circle at 50% 60%, rgba(255,180,80,0.85) 0%, rgba(255,130,40,0.35) 35%, transparent 75%)',
              filter: 'blur(6px)',
              mixBlendMode: 'screen',
              willChange: 'opacity',
            }}
          />
          {/* Flame body */}
          <div
            ref={(el) => { flameRefs.current[i] = el; }}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: `${c.size / 16}cqw`,
              height: `${(c.size * 2) / 16}cqw`,
              background: 'radial-gradient(ellipse 55% 75% at 50% 78%, rgba(255,230,150,0.95) 0%, rgba(255,170,60,0.78) 28%, rgba(255,110,30,0.38) 60%, transparent 95%)',
              filter: 'blur(1.2px)',
              mixBlendMode: 'screen',
              transformOrigin: 'bottom center',
              willChange: 'opacity, transform',
            }}
          />
        </div>
      ))}
    </>
  );
}
